import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, Send, X, Loader2, ChevronLeft,
  User, Calendar, Clock, AlertTriangle, Flag, MoreVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { messagesApi } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// Helper to get API URL - uses current origin in production
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};
const API_URL = getApiUrl();

// Message bubble component
const MessageBubble = ({ message, isOwn }) => {
  const { i18n } = useTranslation();
  const time = new Date(message.created_at).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}
    >
      <div className={`max-w-[75%] ${isOwn ? 'order-2' : 'order-1'}`}>
        <div
          className={`px-4 py-2 rounded-2xl ${
            isOwn
              ? 'bg-primary text-white rounded-br-md'
              : 'bg-gray-100 text-gray-900 rounded-bl-md'
          } ${message.flagged ? 'border-2 border-amber-400' : ''}`}
        >
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        </div>
        {message.flagged && (
          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
            <AlertTriangle className="w-3 h-3 text-amber-500" />
            <span className="text-xs text-amber-600">
              {i18n.language === 'fr' ? 'Contact masqué' : 'Contact hidden'}
            </span>
          </div>
        )}
        <p className={`text-xs text-gray-400 mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
          {time}
        </p>
      </div>
    </motion.div>
  );
};

// Conversation list item
const ConversationItem = ({ conversation, onClick, isActive }) => {
  const { i18n } = useTranslation();
  
  return (
    <motion.div
      whileHover={{ backgroundColor: 'rgba(249, 115, 22, 0.05)' }}
      onClick={onClick}
      className={`p-4 border-b cursor-pointer transition-colors ${
        isActive ? 'bg-orange-50 border-l-4 border-l-primary' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm truncate">{conversation.other_party_name}</h4>
            {conversation.unread_count > 0 && (
              <span className="bg-primary text-white text-xs rounded-full px-2 py-0.5 ml-2">
                {conversation.unread_count}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate">{conversation.experience_title}</p>
          {conversation.last_message && (
            <p className="text-xs text-gray-400 truncate mt-1">
              {conversation.last_message.content}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Chat window component
const ChatWindow = ({ bookingId, onBack, otherPartyName, experienceTitle, otherPartyId }) => {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSending, setReportSending] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (bookingId) {
      fetchMessages();
      // Poll for new messages every 10 seconds
      const interval = setInterval(fetchMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [bookingId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const data = await messagesApi.getByBooking(bookingId);
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await messagesApi.send(bookingId, newMessage.trim());
      setMessages(prev => [...prev, response]);
      setNewMessage('');
      
      // Show warning if message was flagged/sanitized
      if (response.warning) {
        toast.warning(response.warning, { duration: 6000 });
      }
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur lors de l\'envoi' : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleReport = async () => {
    if (!reportReason.trim()) {
      toast.error(i18n.language === 'fr' ? 'Veuillez sélectionner une raison' : 'Please select a reason');
      return;
    }

    setReportSending(true);
    try {
      const token = localStorage.getItem('vgt_token');
      const response = await fetch(`${API_URL}/api/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reported_user_id: otherPartyId,
          reason: reportReason,
          details: reportDetails,
          booking_id: bookingId
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(i18n.language === 'fr' 
          ? 'Signalement envoyé. Notre équipe va examiner votre demande.' 
          : 'Report submitted. Our team will review your request.');
        setShowReportModal(false);
        setReportReason('');
        setReportDetails('');
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur lors de l\'envoi' : 'Failed to send report');
    } finally {
      setReportSending(false);
    }
  };

  const reportReasons = [
    { value: 'inappropriate_behavior', label: i18n.language === 'fr' ? 'Comportement inapproprié' : 'Inappropriate behavior' },
    { value: 'harassment', label: i18n.language === 'fr' ? 'Harcèlement' : 'Harassment' },
    { value: 'scam', label: i18n.language === 'fr' ? 'Arnaque / Fraude' : 'Scam / Fraud' },
    { value: 'no_show', label: i18n.language === 'fr' ? 'Non-présentation' : 'No-show' },
    { value: 'safety_concern', label: i18n.language === 'fr' ? 'Problème de sécurité' : 'Safety concern' },
    { value: 'other', label: i18n.language === 'fr' ? 'Autre' : 'Other' }
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b bg-white flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="md:hidden">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <User className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold">{otherPartyName}</h3>
          <p className="text-xs text-gray-500">{experienceTitle}</p>
        </div>
        
        {/* Report Menu */}
        <div className="relative">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowMenu(!showMenu)}
            className="text-gray-500 hover:text-gray-700"
          >
            <MoreVertical className="w-5 h-5" />
          </Button>
          
          {showMenu && (
            <div className="absolute right-0 top-10 bg-white border rounded-lg shadow-lg py-1 z-50 min-w-[160px]">
              <button
                onClick={() => {
                  setShowReportModal(true);
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                data-testid="report-user-btn"
              >
                <Flag className="w-4 h-4" />
                {i18n.language === 'fr' ? 'Signaler' : 'Report'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Report Modal */}
      <AnimatePresence>
        {showReportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowReportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Flag className="w-5 h-5 text-red-500" />
                  {i18n.language === 'fr' ? 'Signaler un utilisateur' : 'Report user'}
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setShowReportModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                {i18n.language === 'fr' 
                  ? `Signaler ${otherPartyName} pour comportement inapproprié.`
                  : `Report ${otherPartyName} for inappropriate behavior.`}
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {i18n.language === 'fr' ? 'Raison du signalement *' : 'Reason for report *'}
                  </label>
                  <select
                    value={reportReason}
                    onChange={(e) => setReportReason(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">{i18n.language === 'fr' ? 'Sélectionnez une raison' : 'Select a reason'}</option>
                    {reportReasons.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    {i18n.language === 'fr' ? 'Détails (optionnel)' : 'Details (optional)'}
                  </label>
                  <Textarea
                    value={reportDetails}
                    onChange={(e) => setReportDetails(e.target.value)}
                    placeholder={i18n.language === 'fr' 
                      ? 'Décrivez ce qui s\'est passé...'
                      : 'Describe what happened...'}
                    rows={4}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowReportModal(false)}
                  >
                    {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    onClick={handleReport}
                    disabled={reportSending || !reportReason}
                  >
                    {reportSending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Flag className="w-4 h-4 mr-2" />
                    )}
                    {i18n.language === 'fr' ? 'Envoyer' : 'Submit'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <MessageCircle className="w-12 h-12 mb-3" />
            <p>{i18n.language === 'fr' ? 'Aucun message' : 'No messages yet'}</p>
            <p className="text-sm">{i18n.language === 'fr' ? 'Commencez la conversation !' : 'Start the conversation!'}</p>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <MessageBubble
                key={msg.message_id}
                message={msg}
                isOwn={msg.sender_id === user?.user_id}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="p-4 border-t bg-white">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={i18n.language === 'fr' ? 'Votre message...' : 'Your message...'}
            className="flex-1"
            disabled={sending}
            data-testid="message-input"
          />
          <Button type="submit" disabled={!newMessage.trim() || sending} data-testid="send-message-btn">
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Main Messaging component
const MessagingPanel = ({ isOpen, onClose }) => {
  const { i18n } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen]);

  const fetchConversations = async () => {
    try {
      const data = await messagesApi.getConversations();
      setConversations(data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
  };

  const handleBack = () => {
    setSelectedConversation(null);
    fetchConversations(); // Refresh unread counts
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Panel Header */}
          <div className="p-4 border-b flex items-center justify-between bg-primary text-white">
            <h2 className="font-semibold flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              {i18n.language === 'fr' ? 'Messages' : 'Messages'}
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="h-[calc(100%-64px)]">
            {selectedConversation ? (
              <ChatWindow
                bookingId={selectedConversation.booking_id}
                otherPartyName={selectedConversation.other_party_name}
                otherPartyId={selectedConversation.other_party_id}
                experienceTitle={selectedConversation.experience_title}
                onBack={handleBack}
              />
            ) : (
              <div className="h-full overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
                    <MessageCircle className="w-16 h-16 mb-4" />
                    <p className="text-center">
                      {i18n.language === 'fr' 
                        ? 'Aucune conversation. Réservez une expérience pour commencer !'
                        : 'No conversations. Book an experience to start!'}
                    </p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <ConversationItem
                      key={conv.booking_id}
                      conversation={conv}
                      onClick={() => handleSelectConversation(conv)}
                      isActive={selectedConversation?.booking_id === conv.booking_id}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Floating message button with unread count
export const MessageButton = ({ onClick }) => {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const data = await messagesApi.getUnreadCount();
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      // Ignore - user might not be logged in
    }
  };

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className="relative p-3 bg-primary text-white rounded-full shadow-lg hover:bg-primary/90 transition-colors"
      data-testid="message-button"
    >
      <MessageCircle className="w-6 h-6" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </motion.button>
  );
};

export default MessagingPanel;
